/**
 * Cash-basis waterfall, by payment, grouped per week.
 *
 * WHY (2026-08-30, Fan): "I would like to see how much I am supposed to pay in
 * taxes per week by invoice, so I have the full amount. But then I can only put
 * into reserve what I have received... Like this I will know what goes into
 * working capital, the net VAT that is due, the estimated corp tax (15%), and
 * the profit share I can take (50%, the other 50% for overhead)."
 *
 * Cash received to date only: a payment that has not arrived cannot fund a
 * reserve, so nothing future appears here by design. `VAT unfunded` is the gap
 * between VAT owed on the invoices and VAT the received cash can cover.
 */
import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Loader2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { brosTaxReservesAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';

function num(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function Line({ label, value, currency, strong, muted, hint }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-0.5">
      <span className={`text-xs ${muted ? 'text-muted-foreground' : ''}`} title={hint}>
        {label}
      </span>
      <span className={`tabular-nums text-sm ${strong ? 'font-semibold' : ''}`}>
        {formatCurrency(num(value), currency)}
      </span>
    </div>
  );
}

function PeriodRow({ period, currency, data }) {
  const [open, setOpen] = useState(false);
  const unfunded = num(data.vat_unfunded);

  return (
    <>
      <tr
        className="border-b hover:bg-muted/40 cursor-pointer"
        onClick={() => setOpen((prior) => !prior)}
        data-testid={`waterfall-${period}-${currency}`}
      >
        <td className="py-2 pl-2">
          <span className="inline-flex items-center gap-1 font-medium">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {period}
          </span>
        </td>
        <td className="py-2 font-mono text-xs">{currency}</td>
        <td className="py-2 text-right tabular-nums">{formatCurrency(num(data.received), currency)}</td>
        <td className="py-2 text-right tabular-nums">{formatCurrency(num(data.net_vat_due), currency)}</td>
        <td className="py-2 text-right tabular-nums">{formatCurrency(num(data.supplier_cost), currency)}</td>
        <td className="py-2 text-right tabular-nums">{formatCurrency(num(data.corp_tax), currency)}</td>
        <td className="py-2 text-right tabular-nums font-semibold text-green-700 dark:text-green-500">
          {formatCurrency(num(data.draw_available), currency)}
        </td>
        <td className="py-2 text-right text-xs text-muted-foreground">
          {data.payment_count}
          {unfunded > 0 && (
            <Badge variant="outline" className="ml-1 text-[10px] text-amber-600">
              {formatCurrency(unfunded, currency)} unfunded
            </Badge>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b bg-muted/20">
          <td colSpan={8} className="px-4 py-3 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
              <div>
                <Line label="Cash received" value={data.received} currency={currency} strong />
                <Line label="Output VAT on that cash" value={data.output_vat} currency={currency} muted />
                <Line label="Input VAT (period share)" value={data.input_vat} currency={currency} muted />
                <Line label="Net VAT due" value={data.net_vat_due} currency={currency} strong />
                <Line
                  label="VAT owed on the invoices"
                  value={data.vat_due_on_invoices}
                  currency={currency}
                  muted
                  hint="Full liability on these invoices, whether or not the client has paid."
                />
                <Line
                  label="Of which unfunded"
                  value={data.vat_unfunded}
                  currency={currency}
                  muted
                  hint="VAT owed that the received cash does not cover yet."
                />
              </div>
              <div>
                <Line
                  label="Supplier cost (working capital)"
                  value={data.supplier_cost}
                  currency={currency}
                />
                {data.supplier_spend_evidenced_paid_this_month != null && (
                  <Line
                    label="Supplier spend evidenced paid this month"
                    value={data.supplier_spend_evidenced_paid_this_month}
                    currency={currency}
                    muted
                    hint="From supplier documents marked paid. BR OS carries no supplier-payment status per booking."
                  />
                )}
                <Line label="Profit before tax" value={data.profit_before_tax} currency={currency} strong />
                <Line label="Corp tax" value={data.corp_tax} currency={currency} />
                <Line label="Profit after tax" value={data.profit_after_tax} currency={currency} />
                <Line label="Draw available (50%)" value={data.draw_available} currency={currency} strong />
                <Line label="Held for overhead (50%)" value={data.overhead_retained} currency={currency} muted />
                {num(data.received_with_unresolved_supplier) > 0 && (
                  <Line
                    label="Cash excluded: supplier cost unknown"
                    value={data.received_with_unresolved_supplier}
                    currency={currency}
                    muted
                    hint="These payments fund no draw until their supplier cost is known."
                  />
                )}
              </div>
            </div>

            {data.payments?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Payments in this period
                </p>
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium py-1">Invoice</th>
                      <th className="text-left font-medium py-1">Date</th>
                      <th className="text-left font-medium py-1">Client</th>
                      <th className="text-right font-medium py-1">Received</th>
                      <th className="text-right font-medium py-1">Output VAT</th>
                      <th className="text-right font-medium py-1">Supplier cost</th>
                      <th className="text-right font-medium py-1">VAT unfunded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payments.map((payment) => (
                      <tr key={`${payment.invoice_ref}-${payment.date}`} className="border-t">
                        <td className="py-1 font-mono">{payment.invoice_ref}</td>
                        <td className="py-1">{payment.date}</td>
                        <td className="py-1">{payment.client ?? '\u2014'}</td>
                        <td className="py-1 text-right tabular-nums">
                          {formatCurrency(num(payment.received), currency)}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {formatCurrency(num(payment.output_vat), currency)}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {payment.supplier_cost == null
                            ? <span className="text-amber-600">unknown</span>
                            : formatCurrency(num(payment.supplier_cost), currency)}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {formatCurrency(num(payment.vat_unfunded), currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function BROSCashWaterfall() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [group, setGroup] = useState('week');
  const [corpTaxPct, setCorpTaxPct] = useState('15');
  const [drawPct, setDrawPct] = useState('50');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await brosTaxReservesAPI.getCashWaterfall({
        group,
        corp_tax_pct: corpTaxPct,
        draw_pct: drawPct,
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [group, corpTaxPct, drawPct]);

  useEffect(() => { load(); }, [load]);

  const rows = [];
  (data?.periods ?? []).slice().reverse().forEach((period) => {
    Object.entries(period.by_currency ?? {}).forEach(([currency, currencyData]) => {
      rows.push({ period: period.period, currency, data: currencyData });
    });
  });

  return (
    <Card data-testid="bros-cash-waterfall">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Cash waterfall</CardTitle>
            <CardDescription className="text-xs">
              Received cash split into net VAT, supplier cost, corp tax and your
              draw. Cash received to date only &mdash; nothing future is reserved.
            </CardDescription>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground block">Corp tax %</label>
              <Input
                value={corpTaxPct}
                onChange={(event) => setCorpTaxPct(event.target.value)}
                className="h-8 w-16 text-sm"
                data-testid="waterfall-corp-tax-pct"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground block">Draw %</label>
              <Input
                value={drawPct}
                onChange={(event) => setDrawPct(event.target.value)}
                className="h-8 w-16 text-sm"
                data-testid="waterfall-draw-pct"
              />
            </div>
            <Button
              size="sm"
              variant={group === 'week' ? 'default' : 'outline'}
              onClick={() => setGroup('week')}
            >
              Weekly
            </Button>
            <Button
              size="sm"
              variant={group === 'month' ? 'default' : 'outline'}
              onClick={() => setGroup('month')}
            >
              Monthly
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.keys(data?.totals_by_currency ?? {}).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(data.totals_by_currency).map(([currency, totals]) => (
              <div
                key={currency}
                className="rounded-lg border p-3"
                data-testid={`waterfall-total-${currency}`}
              >
                <p className="text-xs font-mono text-muted-foreground mb-1">{currency} &middot; to date</p>
                <Line label="Received" value={totals.received} currency={currency} strong />
                <Line label="Net VAT due" value={totals.net_vat_due} currency={currency} />
                <Line label="Supplier cost" value={totals.supplier_cost} currency={currency} muted />
                <Line label="Corp tax" value={totals.corp_tax} currency={currency} />
                <Line label="Draw available" value={totals.draw_available} currency={currency} strong />
                <Line label="Overhead" value={totals.overhead_retained} currency={currency} muted />
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground flex items-start gap-1">
          <Info className="h-3 w-3 mt-0.5 shrink-0" />
          {data?.profit_basis}
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-destructive" data-testid="waterfall-error">{error}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="text-left font-medium py-1 pl-2">{group === 'week' ? 'Week' : 'Month'}</th>
                <th className="text-left font-medium py-1">Currency</th>
                <th className="text-right font-medium py-1">Received</th>
                <th className="text-right font-medium py-1">Net VAT due</th>
                <th className="text-right font-medium py-1">Supplier cost</th>
                <th className="text-right font-medium py-1">Corp tax</th>
                <th className="text-right font-medium py-1">Draw</th>
                <th className="text-right font-medium py-1">Payments</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <PeriodRow
                  key={`${row.period}-${row.currency}`}
                  period={row.period}
                  currency={row.currency}
                  data={row.data}
                />
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

export default BROSCashWaterfall;
